import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { applyDesignTheme } from '@/lib/theme';
import {
  type AdminBranding,
  BRANDING_FAVICON_ALLOWED_MIMES,
  BRANDING_FAVICON_MAX_BYTES,
  BRANDING_LOGO_ALLOWED_MIMES,
  BRANDING_LOGO_MAX_BYTES,
  DESIGN_THEMES,
  type DesignThemeId,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useRef, useState } from 'react';

export function AdminBrandingPage() {
  const cfg = useQuery({
    queryKey: ['admin', 'branding'],
    queryFn: () => api.adminBranding(),
  });

  return (
    <PageShell
      header={
        <PageHeader
          title="브랜딩"
          description="사용자 화면 좌측 상단의 회사 로고와 서비스 이름. 설정하지 않으면 기본 로고와 'innermate' 가 표시됩니다. 관리자 화면(/admin)에는 영향이 없습니다."
        />
      }
    >
      {cfg.isLoading ? (
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      ) : cfg.error || !cfg.data ? (
        <p className="text-sm text-destructive">
          설정을 불러올 수 없습니다:{' '}
          {cfg.error instanceof ApiError ? cfg.error.message : '알 수 없는 오류'}
        </p>
      ) : (
        <div className="mx-auto max-w-2xl space-y-6">
          <ServiceNameCard data={cfg.data} />
          <ChatFooterCard data={cfg.data} />
          <LogoCard data={cfg.data} />
          <FaviconCard data={cfg.data} />
          <DesignThemeCard data={cfg.data} />
        </div>
      )}
    </PageShell>
  );
}

function ServiceNameCard({ data }: { data: AdminBranding }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(data.serviceName ?? '');
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setValue(data.serviceName ?? '');
  }, [data]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'branding'] });
    qc.invalidateQueries({ queryKey: ['branding'] });
  };

  const mut = useMutation({
    mutationFn: (serviceName: string | null) => api.adminUpdateBranding({ serviceName }),
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      invalidate();
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '저장 실패');
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = value.trim();
    mut.mutate(trimmed.length === 0 ? null : trimmed);
  };

  const onReset = () => {
    setValue('');
    mut.mutate(null);
  };

  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold">서비스 이름</h2>
        <p className="text-xs text-foreground-muted mt-1">
          사용자 화면 좌측 상단에 표시됩니다. 비워두고 저장하면 기본값('innermate')으로 돌아갑니다.
        </p>
      </header>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="회사 또는 서비스 이름" htmlFor="branding-service-name">
          <Input
            id="branding-service-name"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="innermate"
            maxLength={100}
          />
        </Field>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {savedAt && !error ? <p className="text-sm text-success">저장되었습니다.</p> : null}
        <div className="flex items-center justify-end gap-2">
          {data.serviceName ? (
            <Button type="button" variant="ghost" onClick={onReset} disabled={mut.isPending}>
              기본값으로 되돌리기
            </Button>
          ) : null}
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? '저장 중…' : '저장'}
          </Button>
        </div>
      </form>
    </section>
  );
}

function ChatFooterCard({ data }: { data: AdminBranding }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(data.chatFooterText ?? '');
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setValue(data.chatFooterText ?? '');
  }, [data]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'branding'] });
    qc.invalidateQueries({ queryKey: ['branding'] });
  };

  const mut = useMutation({
    mutationFn: (body: { chatFooterText?: string | null; chatFooterEnabled?: boolean }) =>
      api.adminUpdateBranding(body),
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      invalidate();
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '저장 실패');
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = value.trim();
    mut.mutate({ chatFooterText: trimmed.length === 0 ? null : trimmed });
  };

  const onReset = () => {
    setValue('');
    mut.mutate({ chatFooterText: null });
  };

  const enabled = data.chatFooterEnabled;
  const preview = value.trim();

  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">채팅창 하단 안내 문구</h2>
          <p className="text-xs text-foreground-muted mt-1">
            채팅 입력창 아래 가운데에 표시됩니다. 표시하지 않으려면 끄거나, 비워두고 저장하세요.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="안내 문구 표시 켜기/끄기"
          onClick={() => mut.mutate({ chatFooterEnabled: !enabled })}
          disabled={mut.isPending}
          className={cn(
            'shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            enabled
              ? 'border-transparent bg-accent text-accent-foreground'
              : 'border-border bg-surface-muted text-foreground-muted hover:text-foreground',
          )}
        >
          <span
            className={cn(
              'size-1.5 rounded-full',
              enabled ? 'bg-accent-foreground' : 'bg-foreground-muted',
            )}
            aria-hidden
          />
          {enabled ? '표시 켜짐' : '표시 꺼짐'}
        </button>
      </header>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="안내 문구" htmlFor="branding-chat-footer">
          <Input
            id="branding-chat-footer"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="AI 답변은 부정확할 수 있습니다. 중요한 내용은 다시 확인하세요."
            maxLength={300}
          />
        </Field>
        {preview ? (
          <div className="rounded-lg border border-border-muted bg-surface-muted/40 px-3 py-2">
            <div className="text-[11px] text-foreground-faint mb-1">미리보기</div>
            <p className="text-center text-sm text-foreground-muted whitespace-pre-wrap break-words">
              {preview}
            </p>
          </div>
        ) : null}
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {savedAt && !error ? <p className="text-sm text-success">저장되었습니다.</p> : null}
        <div className="flex items-center justify-end gap-2">
          {data.chatFooterText ? (
            <Button type="button" variant="ghost" onClick={onReset} disabled={mut.isPending}>
              문구 지우기
            </Button>
          ) : null}
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? '저장 중…' : '저장'}
          </Button>
        </div>
      </form>
    </section>
  );
}

function LogoCard({ data }: { data: AdminBranding }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // 캐시 버스팅 — updatedAt 이 변하면 새 URL 로 미리보기 강제 갱신.
  const previewUrl = data.hasCustomLogo
    ? `/api/branding/logo?v=${data.logoUpdatedAt ? Math.floor(new Date(data.logoUpdatedAt).getTime() / 1000) : Date.now()}`
    : '/logo.png';

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'branding'] });
    qc.invalidateQueries({ queryKey: ['branding'] });
  };

  const uploadMut = useMutation({
    mutationFn: (file: File) => api.adminUploadBrandingLogo(file),
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      invalidate();
      if (inputRef.current) inputRef.current.value = '';
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '업로드 실패');
      if (inputRef.current) inputRef.current.value = '';
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => api.adminDeleteBrandingLogo(),
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      invalidate();
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '삭제 실패');
    },
  });

  const onPick = () => {
    setError(null);
    inputRef.current?.click();
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!(BRANDING_LOGO_ALLOWED_MIMES as readonly string[]).includes(file.type)) {
      setError('PNG / JPEG / WebP / SVG 파일만 업로드할 수 있습니다.');
      e.target.value = '';
      return;
    }
    if (file.size > BRANDING_LOGO_MAX_BYTES) {
      setError('파일 크기가 2MB 를 초과합니다.');
      e.target.value = '';
      return;
    }
    uploadMut.mutate(file);
  };

  const pending = uploadMut.isPending || deleteMut.isPending;

  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold">로고</h2>
        <p className="text-xs text-foreground-muted mt-1">
          1:1 비율 정사각형 권장 (예: 256×256). PNG / JPEG / WebP / SVG, 최대 2MB.
        </p>
      </header>
      <div className="flex items-center gap-6">
        <div className="size-24 rounded-xl border border-border bg-surface-muted grid place-items-center overflow-hidden">
          <img
            src={previewUrl}
            alt={data.hasCustomLogo ? '업로드된 로고' : '기본 로고'}
            className="size-full object-contain"
          />
        </div>
        <div className="flex-1 space-y-2">
          <p className="text-sm">
            {data.hasCustomLogo ? '커스텀 로고가 적용 중입니다.' : '기본 로고를 사용 중입니다.'}
          </p>
          {data.logoUpdatedAt ? (
            <p className="text-xs text-foreground-muted">
              업로드: {new Date(data.logoUpdatedAt).toLocaleString('ko-KR')}
              {data.logoContentType ? ` · ${data.logoContentType}` : ''}
            </p>
          ) : null}
          <div className="flex items-center gap-2 pt-1">
            <input
              ref={inputRef}
              type="file"
              accept={BRANDING_LOGO_ALLOWED_MIMES.join(',')}
              onChange={onChange}
              className="hidden"
            />
            <Button type="button" onClick={onPick} disabled={pending}>
              {uploadMut.isPending
                ? '업로드 중…'
                : data.hasCustomLogo
                  ? '로고 변경'
                  : '로고 업로드'}
            </Button>
            {data.hasCustomLogo ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => deleteMut.mutate()}
                disabled={pending}
              >
                {deleteMut.isPending ? '삭제 중…' : '기본값으로 되돌리기'}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      {error ? <p className="mt-4 text-sm text-destructive">{error}</p> : null}
      {savedAt && !error ? <p className="mt-4 text-sm text-success">저장되었습니다.</p> : null}
    </section>
  );
}

function FaviconCard({ data }: { data: AdminBranding }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const previewUrl = data.hasCustomFavicon
    ? `/api/branding/favicon?v=${data.faviconUpdatedAt ? Math.floor(new Date(data.faviconUpdatedAt).getTime() / 1000) : Date.now()}`
    : '/favicon.png';

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'branding'] });
    qc.invalidateQueries({ queryKey: ['branding'] });
  };

  const uploadMut = useMutation({
    mutationFn: (file: File) => api.adminUploadBrandingFavicon(file),
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      invalidate();
      if (inputRef.current) inputRef.current.value = '';
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '업로드 실패');
      if (inputRef.current) inputRef.current.value = '';
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => api.adminDeleteBrandingFavicon(),
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      invalidate();
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '삭제 실패');
    },
  });

  const onPick = () => {
    setError(null);
    inputRef.current?.click();
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!(BRANDING_FAVICON_ALLOWED_MIMES as readonly string[]).includes(file.type)) {
      setError('PNG / ICO / SVG 파일만 업로드할 수 있습니다.');
      e.target.value = '';
      return;
    }
    if (file.size > BRANDING_FAVICON_MAX_BYTES) {
      setError('파일 크기가 2MB 를 초과합니다.');
      e.target.value = '';
      return;
    }
    uploadMut.mutate(file);
  };

  const pending = uploadMut.isPending || deleteMut.isPending;

  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold">파비콘</h2>
        <p className="text-xs text-foreground-muted mt-1">
          브라우저 탭/북마크에 표시되는 작은 아이콘. 32×32 이상의 정사각형 권장. PNG / ICO / SVG,
          최대 2MB.
        </p>
      </header>
      <div className="flex items-center gap-6">
        <div className="size-24 rounded-xl border border-border bg-surface-muted flex items-center justify-center overflow-hidden">
          {/* max-w/h + 명시적 width/height = 'auto' — 이미지가 박스보다 작으면 원본 크기,
              크면 박스에 맞춰 축소. object-contain 은 fallback (혹시 max 가 적용 안 될 때). */}
          <img
            src={previewUrl}
            alt={data.hasCustomFavicon ? '업로드된 파비콘' : '기본 파비콘'}
            className="max-w-full max-h-full w-auto h-auto object-contain"
          />
        </div>
        <div className="flex-1 space-y-2">
          <p className="text-sm">
            {data.hasCustomFavicon
              ? '커스텀 파비콘이 적용 중입니다.'
              : '기본 파비콘을 사용 중입니다.'}
          </p>
          {data.faviconUpdatedAt ? (
            <p className="text-xs text-foreground-muted">
              업로드: {new Date(data.faviconUpdatedAt).toLocaleString('ko-KR')}
              {data.faviconContentType ? ` · ${data.faviconContentType}` : ''}
            </p>
          ) : null}
          <div className="flex items-center gap-2 pt-1">
            <input
              ref={inputRef}
              type="file"
              accept={BRANDING_FAVICON_ALLOWED_MIMES.join(',')}
              onChange={onChange}
              className="hidden"
            />
            <Button type="button" onClick={onPick} disabled={pending}>
              {uploadMut.isPending
                ? '업로드 중…'
                : data.hasCustomFavicon
                  ? '파비콘 변경'
                  : '파비콘 업로드'}
            </Button>
            {data.hasCustomFavicon ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => deleteMut.mutate()}
                disabled={pending}
              >
                {deleteMut.isPending ? '삭제 중…' : '기본값으로 되돌리기'}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      {error ? <p className="mt-4 text-sm text-destructive">{error}</p> : null}
      {savedAt && !error ? <p className="mt-4 text-sm text-success">저장되었습니다.</p> : null}
    </section>
  );
}

function DesignThemeCard({ data }: { data: AdminBranding }) {
  const qc = useQueryClient();
  const [value, setValue] = useState<DesignThemeId>(data.designTheme);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setValue(data.designTheme);
  }, [data.designTheme]);

  const mut = useMutation({
    mutationFn: (designTheme: DesignThemeId) => api.adminUpdateBranding({ designTheme }),
    onSuccess: (next) => {
      setError(null);
      setSavedAt(Date.now());
      // useBranding 재요청을 기다리지 않고 즉시 <html> 클래스 갱신 → 같은 페이지에서 바로 전환.
      applyDesignTheme(next.designTheme);
      qc.invalidateQueries({ queryKey: ['admin', 'branding'] });
      qc.invalidateQueries({ queryKey: ['branding'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : '저장 실패');
    },
  });

  const dirty = value !== data.designTheme;

  return (
    <section className="bg-surface border border-border rounded-2xl p-6">
      <header className="mb-4">
        <h2 className="text-lg font-semibold">디자인</h2>
        <p className="text-xs text-foreground-muted mt-1">
          전체 사용자 화면(관리자 포함)의 색상 팔레트를 선택합니다. 라이트/다크 모드는 사용자별
          설정으로 유지됩니다. 새 디자인은 코드 추가가 필요해 자동으로 늘어나지 않습니다.
        </p>
      </header>
      <fieldset className="space-y-3">
        <legend className="sr-only">디자인 테마</legend>
        {DESIGN_THEMES.map((t) => {
          const selected = value === t.id;
          return (
            <label
              key={t.id}
              className={`flex items-start gap-4 rounded-xl border p-4 cursor-pointer transition ${
                selected
                  ? 'border-accent bg-accent-soft'
                  : 'border-border hover:border-border-muted hover:bg-surface-muted'
              }`}
            >
              <input
                type="radio"
                name="design-theme"
                value={t.id}
                checked={selected}
                onChange={() => setValue(t.id)}
                className="mt-1 accent-[hsl(var(--accent))]"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-sm">{t.label}</span>
                  {t.id === 'default' ? (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-surface-muted text-foreground-muted">
                      기본
                    </span>
                  ) : null}
                </div>
                <p className="text-xs text-foreground-muted">{t.hint}</p>
                <div className="flex items-center gap-1 mt-3">
                  {t.swatch.map((hex, i) => (
                    <span
                      key={`${t.id}-swatch-${i}`}
                      className="inline-block size-5 rounded border border-border"
                      style={{ backgroundColor: hex }}
                      aria-hidden
                    />
                  ))}
                </div>
              </div>
            </label>
          );
        })}
      </fieldset>
      {error ? <p className="mt-4 text-sm text-destructive">{error}</p> : null}
      {savedAt && !error ? <p className="mt-4 text-sm text-success">저장되었습니다.</p> : null}
      <div className="mt-4 flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => setValue(data.designTheme)}
          disabled={!dirty || mut.isPending}
        >
          되돌리기
        </Button>
        <Button type="button" onClick={() => mut.mutate(value)} disabled={!dirty || mut.isPending}>
          {mut.isPending ? '적용 중…' : '적용'}
        </Button>
      </div>
    </section>
  );
}
